import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import OnboardingStepTemplate from '../OnboardingStepTemplate'
import { Box, Button, Text } from '@chakra-ui/react'
import { styled } from 'styled-components'
import { useReduxSelector } from '../../../../redux/baseStore'
import { selectSessionSessions } from '../../../../redux/sessionSlice'
import {
  getOldestSession,
  ONBOARDING_ORGANIZED_SESSION_ID_KEY,
} from '../../../../utils/sessionUtils'
import { useUserContext } from '../../../../contexts/UserContext'
import { GLOBAL_WINDOW_MESSAGE_ENUM, sendGlobalWindowMessage } from '../../../../utils/utils'
import { selectOnboardingProjectId } from '../../../../redux/projectsSlice'
import { WEBAPP_LOCAL_STORAGE_KEYS } from '../../../../models/browserStorage.types'
import { useLocalStorageBoolean } from '../../../../utils/genericHooks'

const Container = styled(Box)`
  .large-text,
  .large-text * {
    font-size: 28px;
    font-weight: 500;
    line-height: 40px;
    color: #000;
    letter-spacing: -1px;

    @media (max-width: 1200px) {
      /* Screens narrower than 1200px */
      font-size: 24px;
      line-height: 36px;
    }
  }
`

interface Props {
  onNext: () => void
}
const OnboardingSessionsOrganizeV3Dialog: FC<Props> = (props) => {
  const { onNext } = props
  const sessions = useReduxSelector(selectSessionSessions)
  const { captureAnalytics } = useUserContext()
  const onboardingProjectId = useReduxSelector(selectOnboardingProjectId)
  const [isSecondaryButtonDisabled, setIsSecondaryButtonDisabled] = useState(true)
  const [hasCompletedOnboardingInThePast] = useLocalStorageBoolean(
    WEBAPP_LOCAL_STORAGE_KEYS.HasCompletedOnboardingInThePast,
    false,
  )
  const revisitedAnalyticsPostfix = hasCompletedOnboardingInThePast ? ':revisted' : ''

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSecondaryButtonDisabled(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  const mostImportant = useMemo(() => {
    if (!sessions || sessions.length === 0) {
      return undefined
    }

    const sessionsWithActiveTabs = sessions.map((session) => {
      const activeTabsCount = session.pages.filter((page) => page.is_open).length
      return { session, activeTabsCount }
    })

    sessionsWithActiveTabs.sort((a, b) => {
      if (b.activeTabsCount === a.activeTabsCount) {
        return b.session.datetime_start_ts - a.session.datetime_start_ts
      }
      return b.activeTabsCount - a.activeTabsCount
    })

    // Ensure the session with less than 10 tabs is prioritized to avoid paywall
    const sessionToSuggest = sessionsWithActiveTabs.find(({ session }) => session.pages.length < 10)

    return sessionToSuggest ? sessionToSuggest.session : sessionsWithActiveTabs[0].session
  }, [sessions])

  useEffect(() => {
    if (mostImportant) {
      localStorage.setItem(ONBOARDING_ORGANIZED_SESSION_ID_KEY, mostImportant.id.toString())
      setTimeout(() => {
        const container = document.querySelector('#tablist-scroll-container')
        const element = document.querySelector(`[data-session-id="${mostImportant?.id}"]`)
        if (element && container) {
          const elementTop = element.getBoundingClientRect().top
          const containerTop = container.getBoundingClientRect().top
          const offset = elementTop - containerTop - 100
          container.scrollTo({
            top: container.scrollTop + offset,
            behavior: 'smooth',
          })
          const existingMostImportant = document.querySelector('[data-session-most-important]')
          if (existingMostImportant) {
            existingMostImportant.removeAttribute('data-session-most-important')
          }
          element.setAttribute('data-session-most-important', 'true')
        }
      }, 500)
    }
  }, [mostImportant])

  const doNext = useCallback(() => {
    const existingMostImportant = document.querySelector('[data-session-most-important]')
    if (existingMostImportant) {
      existingMostImportant.removeAttribute('data-session-most-important')
    }
    const oldest = getOldestSession(sessions)
    if (oldest) {
      onNext()
    } else {
      captureAnalytics(`onboarding_tutorial:skipped_session_cleanup${revisitedAnalyticsPostfix}`, {
        numSmartSessions: sessions?.length ?? 0,
      })
      localStorage.removeItem(ONBOARDING_ORGANIZED_SESSION_ID_KEY)
      sendGlobalWindowMessage(GLOBAL_WINDOW_MESSAGE_ENUM.SKIP_SESSION_CLEANUP_ONBOARDING_STEP)
    }
  }, [sessions, captureAnalytics, revisitedAnalyticsPostfix, onNext])

  return (
    <OnboardingStepTemplate
      phaseNum={4}
      numPhases={6}
      bodyContent={
        <Container>
          <Text className="large-text" mb="4px">
            This &quot;{mostImportant?.name}&quot; session seems important.
          </Text>
          <Text mt="4" className="large-text" mb="4px">
            Let&apos;s add it to your Folders list on the left so you can easily revisit it and
            share it with others.
          </Text>
          <Text
            mt="4"
            className="large-text"
            style={{
              color: '#0171e3',
            }}
            mb="4px"
          >
            Drag and drop it onto the + New Folder button 👈
          </Text>
        </Container>
      }
      buttonText={`That was magic! ✨`}
      onButtonClick={doNext}
      buttonDisabled={!onboardingProjectId}
      secondaryButton={
        !onboardingProjectId ? (
          <>
            <Button
              marginRight="8px"
              size="lg"
              variant="ghost"
              borderRadius="100px"
              paddingRight="24px"
              paddingLeft="24px"
              color={'black'}
              border="1px solid #FFF"
              fontWeight={500}
              _hover={{
                backgroundColor: '#EBEBEB',
              }}
              onClick={doNext}
              isLoading={false}
              isDisabled={isSecondaryButtonDisabled}
            >
              I&apos;ll do this later
            </Button>
          </>
        ) : null
      }
      delayButtonActivation={false}
    />
  )
}

export default OnboardingSessionsOrganizeV3Dialog
